import { subject } from "@casl/ability";
import LastSentChip from "components/shared/ArticlesTable/LastSentChip";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableAPIData, LastElementTableColumns } from "components/shared/LastElementTable/type";
import { useTableColumnView } from "components/shared/LastElementTable/ViewColumnOptions/utils";
import ManagementTableAuthorComponent from "components/shared/ManagementTableAuthorComponent";
import ManagementTableTitleComponent from "components/shared/ManagementTableTitleComponent";
import StatusSelect from "components/shared/StatusSelect";
import type { PaymentOption } from "components/shared/StatusSelect/utils";
import { paymentColorRules, paymentOptions } from "components/shared/StatusSelect/utils";
import { completionStatusColorRules, completionStatusOptions } from "containers/ArticleFormContainer/utils";
import { PortalType } from "contexts/portal.context";
import { SmallBody } from "helpers/TypographyComponents";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { usePermissions } from "hooks/usePermissions";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ManagementArticle } from "store/articleStore";
import { ArticleCompletionStatus } from "store/articleStore";
import { PaymentStatus } from "store/types";

import styles from "./style.module.scss";
import { AgreementSignedStatus } from "./utils";

interface IPressManagementTable {
    /**
     * Variables and functions from APIPagination.
     */
    APIData: LastElementTableAPIData;
    /**
     * Articles data
     */
    articlesData: ManagementArticle[] | undefined;
    /**
     * Loading data
     */
    isLoading: boolean;
    /**
     * All data count
     */
    dataCount: number | undefined;
    /**
     * Handle select table rows
     */
    handleSelectRows: (rowsIds: number[]) => void;
    /**
     * Handle change payment status
     */
    handleChangePaymentStatus: (
        type: "single" | "bulk",
        paidStatus: PaymentStatus,
        articleIds?: number[],
        titleId?: number,
        articleId?: number,
    ) => void;
    /**
     * Handle search query for title/author component
     */
    handleQuery: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /**
     * Hanle open article when clicking on an Article row
     */
    handleShowArticle: (_articleId: number, _id2: any, _type: any, article: ManagementArticle) => void;
    /**
     * uses_agreements_validation_chain company settings
     */
    usesAgreementValidation: boolean;
    /**
     * Update article completion status
     */
    updateArticleCompletionStatus: (
        completionStatus: ArticleCompletionStatus,
        titleId: number,
        articleId: number,
    ) => void;
    /**
     * Drop down select payment options
     */
    dropdownPaymentOptions: PaymentOption[];
}

/**
 * PRESS / AUDIOVISUAL Management table
 */
const PressManagementTable = ({
    APIData,
    articlesData,
    isLoading,
    dataCount,
    handleSelectRows,
    handleChangePaymentStatus,
    handleQuery,
    handleShowArticle,
    usesAgreementValidation,
    updateArticleCompletionStatus,
    dropdownPaymentOptions,
}: IPressManagementTable) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const portalType = useContext(PortalType);
    const { hasPermissions } = usePermissions();
    const ability = useAbility();
    const { showColumn } = useTableColumnView("press-management-table", portalType);

    const disablePaymentSelect = (value: PaymentStatus) => {
        if (value === PaymentStatus.PAID) {
            return ability.cannot("access", subject("ArticlePaymentOptions", { level: 2 }));
        } else {
            return ability.cannot("access", subject("ArticlePaymentOptions", { level: 1 }));
        }
    };

    const articlesColumns: LastElementTableColumns[] = [
        {
            id: "titleName",
            label: t(`PressAudio.Management.Publication_${portalType}`),
            align: "left",
            sort: true,
            format: (value, id, currentRow) => (
                <ManagementTableTitleComponent
                    title={value}
                    isbn={currentRow.titleEan13}
                    titleCoverUrl={currentRow.titleAvatarUrl}
                    handleQuery={handleQuery}
                    nav={() => navigate(`/publications/${currentRow.titleId}`)}
                />
            ),
        },
        {
            id: "beneficiary",
            label: `${t("PressAudio.Management.Beneficiary")}/${t(`PressAudio.Management.Article_${portalType}`)}`,
            align: "left",
            sort: true,
            format: (value, id, currentRow) => (
                <div>
                    <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
                        <ManagementTableAuthorComponent
                            author={value}
                            showAvatar={false}
                            handleQuery={handleQuery}
                            beneficiaryId={currentRow.beneficiaryId}
                            page="management"
                        />
                        {currentRow?.lastSentAt && <LastSentChip article={currentRow} />}
                    </div>
                    <SmallBody color="grey">{currentRow.name}</SmallBody>
                </div>
            ),
        },
        {
            id: "contractSigned",
            label: t("PressAudio.Management.Contract"),
            align: "left",
            sort: true,
            format: (value, id, currentRow: ManagementArticle) => {
                return (
                    <AgreementSignedStatus
                        value={value}
                        article={currentRow}
                        usesAgreementValidation={usesAgreementValidation}
                    />
                );
            },
        },
        {
            id: "grossDuty",
            label: t("PressAudio.Management.GrossDuty"),
            align: "left",
            sort: true,
            hidden: !showColumn("grossDuty"),
            format: (value, id, currentRow) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
        {
            id: "netToPay",
            label: t("PressAudio.Management.NetToPay"),
            align: "left",
            sort: true,
            hidden: !showColumn("netToPay"),
            format: (value, id, currentRow) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
        {
            id: "paymentDate",
            label: t("Articles.PaymentDate"),
            align: "left",
            hidden: !showColumn("paymentDate"),
            format: (value: any, id, currentRow) =>
                currentRow.paidStatus === PaymentStatus.PAID
                    ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } })
                    : "-",
        },
        {
            id: "paidStatus",
            sort: true,
            label: t("PressAudio.Management.Payment"),
            align: "left",
            format: (value, id, currentRow) => {
                const { titleId, id: articleId } = currentRow;
                return (
                    <StatusSelect
                        value={value}
                        onChange={(paidStatus) =>
                            handleChangePaymentStatus("single", paidStatus, undefined, titleId, articleId)
                        }
                        options={paymentOptions()}
                        dropdownOptions={dropdownPaymentOptions}
                        disabled={disablePaymentSelect(value)}
                        colorRules={paymentColorRules}
                    />
                );
            },
        },
        {
            id: "completionStatus",
            label: t("Articles.CompletionStatusLabel"),
            align: "left",
            format: (value, id, currentRow) => {
                const { titleId, id: articleId } = currentRow;
                return (
                    <StatusSelect
                        value={value}
                        onChange={(completionStatus) =>
                            updateArticleCompletionStatus(completionStatus, titleId, articleId)
                        }
                        options={completionStatusOptions()}
                        minWidth="123.88px"
                        disabled={!hasPermissions(["article:edit-completion-status"])}
                        colorRules={completionStatusColorRules}
                        hideColorStatus // delete this prop if we want to show color status
                    />
                );
            },
        },
        {
            id: "completionDate",
            label: t("Articles.CompletionDate"),
            align: "left",
            sort: true,
            hidden: !showColumn("completionDate"),
            format: (value: any, id, currentRow) =>
                currentRow.completionStatus === ArticleCompletionStatus.COMPLETED && value
                    ? formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } })
                    : "-",
        },
    ];

    return (
        <div className={styles["press-management-table"]}>
            <LastElementTable
                APIData={APIData}
                columns={articlesColumns}
                dataType={["press"].includes(portalType) ? "articles" : "articles_audiovisual"}
                data={articlesData}
                isLoading={isLoading}
                max={-1}
                selectable
                stickyFooter
                onSelectRows={handleSelectRows}
                columnsNumber={articlesColumns.length + 1}
                dataCount={dataCount}
                onRowClick={handleShowArticle}
            />
        </div>
    );
};

export default PressManagementTable;
